import { isNil } from "lodash";
import PropTypes from "prop-types";
import React, { Fragment } from "react";
import ImmutablePropTypes from "react-immutable-proptypes";

import FormButton from "../../../components/form/FormButton";
import FormCheckbox from "../../../components/form/FormCheckbox";
import FormTextField from "../../../components/form/FormTextField";
import ModalField from "../../../components/modal/ModalField";
import ModalGroup from "../../../components/modal/ModalGroup";
import ModalRow from "../../../components/modal/ModalRow";
import { MAX_INT } from "../../../constants/math";
import { PropTypeShapes } from "../../../types";
import preprocessorQuery from "../../../utils/preprocessorQuery";
import userInputValidators from "../../../utils/userInputValidators";

const CalendarSettingsEraDetailView = props => {
  const disabled = isNil(props.selectedItem) || props.ignoreEras;
  const isIos = preprocessorQuery.isIos();

  const index = props.selectedItem;
  const e = props.eras.get(index);
  if (isNil(index)) {
    return null;
  }

  const isFirst = index === 0;
  const isLast = index === props.eras.count() - 1;
  const isInfinite = e.duration >= MAX_INT;
  const isBackwards = isFirst ? true : isLast ? false : e.isBackwards;

  const leap1 = props.leapCycles.get(0);
  const canHaveLeapYears = Boolean(leap1);

  const leapExamples = canHaveLeapYears
    ? e.leapOffset
      ? [e.leapOffset, leap1 + e.leapOffset, leap1 * 2 + e.leapOffset]
      : [leap1, leap1 * 2, leap1 * 3]
    : null;

  return (
    <Fragment>
      <ModalGroup>
        <ModalRow>
          <ModalField label="Name">
            <FormTextField
              value={e.name}
              autoFocusOnEmpty={true}
              disabled={disabled}
              onChange={label => props.onEraNameChange(index, label)}
              validator={userInputValidators.noEmpty}
            />
          </ModalField>
          <ModalField label="Abbreviated Name">
            <FormTextField
              value={e.shortName}
              disabled={disabled}
              onChange={label => props.onEraShortNameChange(index, label)}
              validator={userInputValidators.noEmpty}
            />
          </ModalField>
        </ModalRow>
        <ModalRow>
          <ModalField label="Duration">
            {isInfinite ? (
              <span className="description infiniteLabel infinite disabled">Infinite</span>
            ) : (
              <FormTextField
                value={e.duration}
                numeric={true}
                isInteger={true}
                onChange={duration => props.onEraDurationChange(index, duration)}
                min={1}
                max={MAX_INT - 1}
                disabled={disabled || !props.canEditDateData}
              />
            )}
          </ModalField>
          <ModalField />
        </ModalRow>
        <ModalRow>
          <ModalField>
            <FormCheckbox
              label="Era is backwards (e.g. BC)"
              checkboxState={isBackwards}
              onChange={value => props.onEraIsBackwardsChange(index, value)}
              disabled={disabled || isInfinite || !props.canEditDateData}
            />
          </ModalField>
        </ModalRow>
        <ModalRow>
          <ModalField>
            <FormCheckbox
              label="Era contains leap years"
              checkboxState={e.hasLeapYears}
              onChange={value => props.onEraHasLeapYearsChange(index, value)}
              disabled={disabled || !canHaveLeapYears || !props.canEditDateData}
            />
          </ModalField>
        </ModalRow>
        <ModalRow>
          <ModalField label="Leap year starting offset">
            <FormTextField
              value={e.leapOffset}
              numeric={true}
              isInteger={true}
              onChange={offset => props.onEraLeapOffsetChange(index, offset)}
              min={0}
              max={leap1 - 1}
              disabled={disabled || !props.canEditDateData || !e.hasLeapYears}
            />
            <span className="editLeapYearsHint">{leapExamples ? `(${leapExamples.join(",")}...)` : ""}</span>
          </ModalField>
        </ModalRow>
      </ModalGroup>
      {isIos && props.canDelete ? (
        <ModalGroup>
          <ModalRow>
            <div className="settingsDetailDeleteButton">
              <FormButton disabled={disabled} destructive={true} label="Delete" onClick={props.onDeleteClick} />
            </div>
          </ModalRow>
        </ModalGroup>
      ) : null}
    </Fragment>
  );
};

CalendarSettingsEraDetailView.propTypes = {
  eras: ImmutablePropTypes.listOf(PropTypeShapes.era).isRequired,
  leapCycles: ImmutablePropTypes.listOf(PropTypes.number).isRequired,
  dispatchLocal: PropTypes.func.isRequired,
  selectedItem: PropTypes.number,
  ignoreEras: PropTypes.bool.isRequired,
  onAddEra: PropTypes.func.isRequired,
  onRemoveEra: PropTypes.func.isRequired,
  onEraNameChange: PropTypes.func.isRequired,
  onEraShortNameChange: PropTypes.func.isRequired,
  onEraDurationChange: PropTypes.func.isRequired,
  onEraIsBackwardsChange: PropTypes.func.isRequired,
  onEraHasLeapYearsChange: PropTypes.func.isRequired,
  onEraLeapOffsetChange: PropTypes.func.isRequired,
  canEditDateData: PropTypes.bool.isRequired,
  canDelete: PropTypes.bool,
  onDeleteClick: PropTypes.func
};

export default CalendarSettingsEraDetailView;
